import { useState } from 'react';
import { todayIn } from '@njpos/core';
import { api, usd } from '../lib/api';
import { downloadCsv } from '../components/DateRange';
import { ErrorNote, useLoad } from '../components/ui';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const thisYear = Number(todayIn().slice(0, 4));
const years = [thisYear - 1, thisYear, thisYear + 1];

export function BudgetEditor() {
  const [year, setYear] = useState(thisYear);
  const data = useLoad(() => api<any[]>(`/api/accounting/budget?year=${year}`), [year]);
  const [edits, setEdits] = useState<Record<string, string[]>>({});
  const [err, setErr] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const rows = data.data ?? [];
  const valueOf = (code: string, m: number, base: number) => (edits[code]?.[m] ?? (base ? (base / 100).toFixed(2) : ''));
  const setCell = (code: string, row: any, m: number, v: string) => { setSaved(false); const cur = edits[code] ?? row.months.map((c: number) => (c ? (c / 100).toFixed(2) : '')); const next = [...cur]; next[m] = v.replace(/[^\d.-]/g, ''); setEdits({ ...edits, [code]: next }); };
  const spread = (code: string, row: any, yearly: string) => { const each = (Number(yearly || 0) / 12).toFixed(2); setSaved(false); setEdits({ ...edits, [code]: Array(12).fill(Number(each) ? each : '') }); void row; };
  async function save() {
    setErr(null); setSaved(false);
    try {
      for (const [code, vals] of Object.entries(edits)) await api('/api/accounting/budget', { method: 'PUT', body: { year, code, months: vals.map((v) => Math.round(Number(v || '0') * 100)) } });
      setEdits({}); setSaved(true); data.reload();
    } catch (e) { setErr(e); }
  }
  const group = (type: string, label: string) => (
    <>
      <tr><td colSpan={14} style={{ fontWeight: 700, paddingTop: 14 }}>{label}</td></tr>
      {rows.filter((r) => r.type === type).map((r) => {
        const cells = r.months.map((c: number, m: number) => valueOf(r.code, m, c));
        const total = cells.reduce((s: number, v: string) => s + Math.round(Number(v || '0') * 100), 0);
        return (
          <tr key={r.code}>
            <td style={{ whiteSpace: 'nowrap' }}>{r.name}</td>
            {cells.map((v: string, m: number) => <td key={m}><input className="n" style={{ width: 70, minWidth: 70 }} inputMode="decimal" value={v} onChange={(e) => setCell(r.code, r, m, e.target.value)} aria-label={`${r.name} ${MONTHS[m]}`} /></td>)}
            <td className="n">{usd(total)}</td>
            <td><button className="ghost" title="Type a yearly amount and spread it evenly" onClick={() => { const y = prompt(`Yearly amount for ${r.name} ($)`, total ? (total / 100).toFixed(2) : ''); if (y !== null) spread(r.code, r, y); }}>Spread</button></td>
          </tr>);
      })}
    </>
  );
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 12, alignItems: 'end' }}>
        <label>Year<select value={year} onChange={(e) => { setYear(Number(e.target.value)); setEdits({}); }}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select></label>
        <button className="primary" disabled={Object.keys(edits).length === 0} onClick={save}>Save budget</button>
        {saved && <span className="chip good">Saved</span>}
      </div>
      <p className="faint">Enter what you plan to take in and spend each month. Use Spread to turn a yearly amount into twelve equal months. Then compare it to what really happened under Reports, Budget vs actual.</p>
      <ErrorNote error={err ?? data.error} />
      <div className="panel"><div className="table-wrap"><table className="grid">
        <thead><tr><th>Account</th>{MONTHS.map((m) => <th key={m} className="n">{m}</th>)}<th className="n">Year</th><th /></tr></thead>
        <tbody>{group('income', 'Income')}{group('expense', 'Expenses')}</tbody>
      </table></div></div>
    </div>
  );
}

export function BudgetVsActual() {
  const t = todayIn();
  const [year, setYear] = useState(thisYear);
  const [month, setMonth] = useState(Number(t.slice(5, 7)));
  const r = useLoad(() => api<any>(`/api/accounting/budget-vs-actual?year=${year}&month=${month}`), [year, month]);
  const rows: any[] = r.data?.rows ?? [];
  // for income, over budget is good; for expenses, over budget is bad
  const tone = (type: string, diff: number) => (diff === 0 ? undefined : (type === 'income') === (diff > 0) ? 'var(--good, #1a7f37)' : 'var(--bad)');
  const part = (type: string, label: string) => {
    const xs = rows.filter((x) => x.type === type);
    if (!xs.length) return null;
    const sum = (k: string) => xs.reduce((s, x) => s + x[k], 0);
    return (
      <>
        <tr><td colSpan={7} style={{ fontWeight: 700, paddingTop: 14 }}>{label}</td></tr>
        {xs.map((x) => { const md = x.month_actual_cents - x.month_budget_cents; const yd = x.ytd_actual_cents - x.ytd_budget_cents; return (
          <tr key={x.code}><td>{x.name}</td><td className="n">{usd(x.month_budget_cents)}</td><td className="n">{usd(x.month_actual_cents)}</td><td className="n" style={{ color: tone(type, md) }}>{usd(md)}</td>
            <td className="n">{usd(x.ytd_budget_cents)}</td><td className="n">{usd(x.ytd_actual_cents)}</td><td className="n" style={{ color: tone(type, yd) }}>{usd(yd)}</td></tr>); })}
        <tr style={{ fontWeight: 700 }}><td>Total {label.toLowerCase()}</td><td className="n">{usd(sum('month_budget_cents'))}</td><td className="n">{usd(sum('month_actual_cents'))}</td><td className="n">{usd(sum('month_actual_cents') - sum('month_budget_cents'))}</td>
          <td className="n">{usd(sum('ytd_budget_cents'))}</td><td className="n">{usd(sum('ytd_actual_cents'))}</td><td className="n">{usd(sum('ytd_actual_cents') - sum('ytd_budget_cents'))}</td></tr>
      </>);
  };
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 12, alignItems: 'end' }}>
        <label>Month<select value={month} onChange={(e) => setMonth(Number(e.target.value))}>{MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select></label>
        <label>Year<select value={year} onChange={(e) => setYear(Number(e.target.value))}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select></label>
        <button onClick={() => downloadCsv(`budget-vs-actual-${year}-${month}.csv`, [['Account', 'Type', 'Month budget', 'Month actual', 'Year budget', 'Year actual'], ...rows.map((x) => [x.name, x.type, x.month_budget_cents / 100, x.month_actual_cents / 100, x.ytd_budget_cents / 100, x.ytd_actual_cents / 100])])}>Download CSV</button>
      </div>
      <ErrorNote error={r.error} />
      <div className="panel"><div className="table-wrap"><table className="grid">
        <thead><tr><th /><th className="n" colSpan={3}>{MONTH_NAMES[month - 1]}</th><th className="n" colSpan={3}>Year so far</th></tr>
          <tr><th>Account</th><th className="n">Budget</th><th className="n">Actual</th><th className="n">Over / under</th><th className="n">Budget</th><th className="n">Actual</th><th className="n">Over / under</th></tr></thead>
        <tbody>{part('income', 'Income')}{part('expense', 'Expenses')}</tbody>
      </table>{r.data && rows.length === 0 && <div className="empty">No budget set for this year yet. Add one under Setup, Budgets.</div>}</div></div>
    </div>
  );
}
